import { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import theme from '../../../theme';
import { Button } from '@material-ui/core';
import { useTranslation } from 'tools/i18n';
import { ErrorContext } from '../../../providers/error';
import useCartStore from '@services/stores/CartStore';
import useSessionStore from '@services/stores/SessionStore';
import Link from "next/link";

const useStyles = makeStyles(() => ({
    couponCodeWrapper: {
        color: 'white',
        background: 'black',
        marginTop: 20,
        padding: 40,
        gridColumn: '1/3',
        borderRadius: 3,
        width: '100%',
        fontSize: 14,
        '&.light': {
            background: '#fff',
            border: '1px solid #222',
            color: '#222',
        },
        '&.selected': {
            padding: '1.5em 2.3em 0.5em',
            '--background': '#edede2',
            background: 'var(--background)',
            borderRadius: '3px',
            color: '#222',
            boxShadow: 'inset 0 0 0px 12px var(--background), inset 0 0 0 14px #ffffffaa',
            '& p::after': {
                content: '"NEWPORT | Selected"',
                margin: '2em 0em 0 auto',
                display: 'block',
                textAlign: 'right',
                fontSize: '1em',
                color: '#656d65',
                fontFamily: 'Chronicle, serif',
            },
        },
        '&.bouquets': {
            padding: '1.5em 2.3em 0.5em',
            '--background': '#66C9BA',
            background: 'var(--background)',
            borderRadius: '3px',
            color: '#222',
            boxShadow: 'inset 0 0 0px 12px var(--background), inset 0 0 0 14px #ffffffaa',
            '& p::after': {
                content: '"NEWPORT | Bouquets"',
                margin: '2em 0em 0 auto',
                display: 'block',
                textAlign: 'right',
                fontSize: '1em',
                color: '#656d65',
                fontFamily: 'Chronicle, serif',
            },
        },
        '&.green-week': {
            background: '#143e35',
        },
        '&.summer-code': {
            background: '#5eafd2',
        },
        '&.anniversary': {
            fontSize: 16,
            background: '#00142c',
            color: '#fbd7a2',
            '& div': {
                marginTop: 0,
            },
            '& h1': {
                marginTop: 0,
                fontFamily: 'Chronicle, serif',
                fontWeight: 600,
                fontStyle: 'italic',
                fontSize: '1.4em',
                textTransform: 'uppercase',
            },
        },
        '& svg': {
            maxWidth: '100%',
        },
    },
    couponCodeApply: {
        marginTop: 10,
        width: '100%',
        ...theme.button.purchase,
        fontSize: 13,
        color: '#000',
        backgroundColor: '#fff',
        textAlign: 'center',
        border: '1px solid white',
        textDecoration: 'none',
        display: 'block',
        '.light &': {
            background: '#222',
            border: '1px solid #222',
            color: '#fff',
        },
        '&.anniversary': {
            background: '#06213d',
            color: '#fbd7a2',
            borderColor: '#fbd7a2',
        },
        '&:hover': {
            ...theme.button.purchaseHover,
            backgroundColor: 'transparent',
            color: '#fff',
            '.light &': {
                background: '#fff',
                border: '1px solid #222',
                color: '#000',
            },
        },

    },
    couponText: {
        margin: '5px 0 20px',
        // whiteSpace: 'pre-line', // flip on if using newlines from translations
        '& ul': {
            padding: 0,
            listStylePosition: 'inside',
        },
        '.light > &': {
            marginTop: '0',
        },
    },
}));

export default function CampaignBlock({ product, prices, productFamily }) {
    const actions = useCartStore((state) => state.methods);
    const sessionActions = useSessionStore((state) => state.methods);
    const { actions: errorActions } = useContext(ErrorContext);
    const classes = useStyles();
    const coupons = actions.getAppliedCouponCodes();
    const { t } = useTranslation([ 'campaigns' ]);
    const [ saleIsOnGoing, setSaleIsOnGoing ] = useState(false);
    const now = new Date();


    useEffect(() => {
        const inviteEnd = new Date('Aug 31, 2025 23:59:00');

        if (now < inviteEnd) {
            setSaleIsOnGoing(true);
        } else {
            // @todo session.isSaleOnGoing
            //setSaleIsOnGoing(hasActiveCoupons(coupons));
        }
    }, [ product.id, coupons ]);

    function isExcludedFromAllCampaigns() {
        return !!product?.customFields?.excluded_from_all_campaigns;
    }

    function triggerNewsletterPopup() {
        window.dispatchEvent(new CustomEvent('intro-popup'));
    }

    async function activateCoupon(code) {
        const result = await actions.applyCoupon(code);
        if (result.status !== 'missing-email') {
            errorActions.addError({
                type: 'success',
                messageKey: 'discount-code-success',
                messageData: {
                    code: code,
                },
            });
        }
    }

    function isRegularPrice() {
        if (prices.listPrice) {
            return false;
        }

        if (product.manufacturer?.name === 'Slim Aarons') {
            return false;
        }

        if (product.productNumber.indexOf('8888-') >= 0) {
            return false;
        }

        return true;
    }

    function hasAllowedProductStreams() {
        const allowedStreamIds = [
            '01931babf0667a4c8da1d8e9fde47ab6',
            '0199b8e1a0f37470a19f55de691646ec',
            '0199b8e87a3b72a1b977468efaded620',
        ].join(',');
        return product.streamIds.filter((id) => allowedStreamIds.includes(id)).length > 0;
    }

    function isSelectedByNewport() {
        return product?.propertyIds?.includes('57c33a2fe7d05e16ac5fddf67be255bc');
    }

    function isOutlet() {
        return product?.categoryIds?.includes('57ada96765bb54639310048c787ff6e8');
    }

    function isChristmasCategory() {
        return product?.categoryIds?.includes('9ae2fdbdcead5e06a8ee2513f886342c');
    }

    function isFauxFur() {
        return product?.propertyIds?.includes('8df4d3e3709c5e79807b979c181deb3f');
    }

    function isBouquets() {
        return product?.categoryIds?.includes('0cc07323d7fe5907bdbbe9ec7c371981');
    }

    function isArtwoodOutdoor() {
        return product?.streamIds?.find((item) => item === '019855fccff37d0c96e4120a03e9ea27');
    }

    if (!isRegularPrice()
        &&!isSelectedByNewport()
        && !isOutlet()
        && !isBouquets()
        && !isExcludedFromAllCampaigns()
        && sessionActions.isSalesChannelNordic()
    ) {
        if (now > new Date('Oct 15, 2025 00:00:00') && now < new Date('Oct 26, 2025 23:59:00')) {
            return <>
                <div className={[ classes.couponCodeWrapper, 'anniversary' ].join(' ')}>
                    <div style={{ marginBottom: 16 }}>
                        <img src="/images/campaigns/anni-horizontal.svg" alt="Anniversary 2025" />
                    </div>
                    <p className={classes.couponText} style={{ marginBottom: 16 }}>{t('campaigns:campaign-block-text-standard')}</p>
                    <Link href="/anniversary-sale">
                        <a href={'/anniversary-sale'}  className={[ classes.couponCodeApply, 'anniversary' ].join(' ')} >
                            {t('campaigns:campaign-block-button-standard')}
                        </a>
                    </Link>
                </div>
            </>;
        }
    }

    if (hasAllowedProductStreams()
        && !isSelectedByNewport()
        && !isExcludedFromAllCampaigns()
        && isRegularPrice()
        && sessionActions.isSalesChannelNordic()
        && now < new Date('Nov 9, 2025 23:59:59')
    ) {
       if (actions.hasActiveCoupons([ 'PRIORITY', 'SMS-VIP', 'FRIEND-VIP' ])) {
           return <>
               <div className={[ classes.couponCodeWrapper, 'black-week' ].join(' ')}>
                   <div style={{ marginBottom: 16 }}>
                       <img src="/images/campaigns/pre-access-black-week-2024.svg" alt="Black Week 2025" />
                   </div>
                   <p className={classes.couponText}>{t('campaigns:campaign-block-text-with-access')}</p>
               </div>
           </>;
       }

       return <>
           <div className={[ classes.couponCodeWrapper, 'black-week' ].join(' ')}>
               <div style={{ marginBottom: 16 }}>
                   <img src="/images/campaigns/pre-access-black-week-2024.svg" alt="Black Week 2025" />
               </div>
               <p className={classes.couponText}>{t('campaigns:campaign-block-text-gain-access')}</p>
               <Button className={[ classes.couponCodeApply, 'black-week' ].join(' ')} onClick={() => {
                   triggerNewsletterPopup();
               }}>
                   {t('account:account-signup')}
               </Button>
           </div>
       </>;
   }

   // RETURNS
   if (
       (!saleIsOnGoing && (!isSelectedByNewport() && !isBouquets()))
       ||
       isRegularPrice() && (!isSelectedByNewport() && !isBouquets())
       ||
       isExcludedFromAllCampaigns() && (!isSelectedByNewport() && !isBouquets())
   ) {
       // DONT SHOW THESE
       return <></>;
   }

    if (
        isSelectedByNewport()
        && (
            sessionActions.isSalesChannelByDomain('se')
            || sessionActions.isSalesChannelByDomain('no')
        )
    ) {
        return <>
            <div className={[classes.couponCodeWrapper, 'selected'].join(' ')}>
                <h3>{t('campaign-block-title-selected')}</h3>
                <p className={classes.couponText} dangerouslySetInnerHTML={{__html: t('campaign-block-text-selected')}}>

                </p>
            </div>
        </>
    }

    if (isOutlet()) {
        //OUTLET
        const now = new Date()
        if (now < new Date('Oct 15, 2025 00:00:00') || now > new Date('Oct 28, 2025 00:00:00')) {
            return ''
        }

       if (!sessionActions.isSalesChannelNordic()) {
           return ''
       }

        return <>
            <div className={[classes.couponCodeWrapper, 'light'].join(' ')}>
                <p className={classes.couponText}>
                    {t('campaign-block-text-outlet')}
                </p>
                <Button className={classes.couponCodeApply} onClick={ ()=>{ activateCoupon('OUT10')} }>
                    {t('campaign-block-button-outlet')}
                </Button>
            </div>
        </>
    }

    if (isChristmasCategory()) {
        //CHRISTMAS CAMPAIGN
        /*
        return <>
            <div className={classes.couponCodeWrapper}>
                <div>
                    <CampaignSVG/>
                </div>
                <p className={classes.couponText}>
                    {t('campaign-block-text-xmas')}
                </p>
                <Button className={classes.couponCodeApply} href={'/super-deals#christmas-deals'}>
                    {t('campaign-block-button-xmas')}
                </Button>
            </div>
        </>

         */
    }

    if (isFauxFur()) {

        if (new Date() < new Date('May 4, 2025 23:59:00')) {
            //FAUX FUR CAMPAIGN
            return <>
                <div className={[ classes.couponCodeWrapper, 'light' ].join(' ')}>
                    <p className={classes.couponText}>
                        {t('campaign-block-text-fauxfur')}
                    </p>
                    <Button className={classes.couponCodeApply} onClick={() => {
                        activateCoupon('FAUXFUR10');
                    }}>
                        {t('campaign-block-button-fauxfur')}
                    </Button>
                </div>
            </>;
        }
    }

    if (isBouquets()) {

        //IS BOUQUETS
        return <>
            <div className={[ classes.couponCodeWrapper, 'bouquets' ].join(' ')}>
                <h3>{t('campaign-block-title-bouquet')}</h3>
                <p className={classes.couponText}
                   dangerouslySetInnerHTML={{ __html: t('campaign-block-text-bouquet') }}></p>
            </div>
        </>;
    }

    if (saleIsOnGoing && !isRegularPrice() &&
        (
            !sessionActions.isSalesChannelByDomain('com')
        )) {
        //REGULAR SALE
        return <>
            <div className={[ classes.couponCodeWrapper, 'summer-code' ].join(' ')}>
                <div>
                    <h1>{t('product-info:summer-code-title')}</h1>
                </div>
                <p className={classes.couponText}
                   dangerouslySetInnerHTML={{ __html: t('product-info:summer-code-text') }}></p>
                <Button className={classes.couponCodeApply} onClick={() => {
                    activateCoupon('SUMMER10');
                }}>
                    {t('product-info:summer-code-activate')}
                </Button>
            </div>
        </>;
    }

    // ANYTHING ELSE
    return <></>;
}
